import { normalizeViText } from "../vi-normalizer.js";
import {
  SLOT_RESOLUTION_REASON,
  slotCandidates,
  slotNone,
  slotResolved,
  type SlotResolution,
  type SlotResolutionReason,
} from "./slot-resolution.js";

/**
 * 🔴 US-AIV2-0022 — ô `fund` · `collection` · `group`: chữ → id của MỘT mục trong PHẠM VI ĐÃ NẠP.
 *
 * MỘT luật khớp cho ba loại ô (không ba bản chép): mục khớp khi
 *   · chữ = đúng `id` (chip gửi id đã liệt kê — sự kiện có cấu trúc), HOẶC
 *   · nhãn chuẩn hoá = chữ chuẩn hoá ("Quỹ lớp 5A" ≡ "quy lop 5a"), HOẶC
 *   · MỌI token của chữ là token NGUYÊN VẸN của nhãn ("lớp 5A" ⊂ "Quỹ lớp 5A"; "tháng 9" ⊂ "Tháng 9/2026").
 * Không substring ("lop 5" ≠ "lop 5a"): sai quỹ là sai sổ.
 *
 * 🔴 IDOR fail-closed nằm ở PHẠM VI, không ở luật khớp: `refs` là danh sách api đã nạp THEO ACTOR (quỹ của nhóm actor
 * là thành viên, đợt thu của các quỹ đó, nhóm actor có ghế). Một id/tên ngoài phạm vi KHÔNG BAO GIỜ thành `resolved`
 * lẫn `candidates` — vì nó không có trong `refs` để mà khớp. Resolver không nhận Prisma, không tự tra gì thêm.
 *
 * Ô trống: 1 mục ⇒ tự điền (`_shared.ts` SLOT_FUND: *"tuỳ chọn khi người dùng chỉ có một quỹ (resolver 0022 tự điền)"*
 * — phiếu xác nhận vẫn in tên quỹ trước Đồng ý); ≥2 ⇒ `candidates/missing` toàn bộ; 0 ⇒ `none` với lý do rỗng (0023d: `emptyReason` của loader nếu có, mặc định `empty_scope`).
 * Có chữ: 1 khớp ⇒ `resolved`; ≥2 ⇒ `candidates/ambiguous` (đúng các mục khớp, thứ tự phạm vi); 0 khớp ⇒
 * `candidates/unmatched` (mời chọn trong phạm vi — người dùng gõ tên chưa đúng, không có nghĩa là không có quỹ).
 * 🔴 Nhãn khớp CHÍNH XÁC thắng nhãn chỉ chứa token (review #815 M2): "Quỹ lớp 5A" gõ đủ tên phải `resolved` dù có "Quỹ dã ngoại
 * lớp 5A" — Người phiên dịch thường gạch chân đúng tên đầy đủ, hỏi lại ở ca phổ biến nhất là hỏi thừa. ≥2 nhãn bằng nhau ⇒ vẫn hỏi.
 */
export interface NamedRef {
  readonly id: string;
  /** Nhãn hiển thị từ sổ thật (tên quỹ · tiêu đề đợt thu hoặc nhãn kỳ · tên nhóm). */
  readonly label: string;
}

/** Chuẩn hoá nhãn/chữ để so token: bỏ dấu, thường, ký tự lạ → khoảng trắng, gộp. KHÔNG bỏ kính ngữ (đây không phải tên người). */
export function normalizeRefText(raw: string): string {
  return normalizeViText(raw ?? "")
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/gu, " ")
    .trim();
}

/** `dotThu` ⇒ "dot thu" — id ô viết camelCase, người (và model) viết thành từ. Chuẩn hoá cùng phép với chữ người gõ. */
export function slotIdAsWords(id: string): string {
  return normalizeRefText(id.replace(/([a-z0-9])([A-Z])/gu, "$1 $2"));
}

/**
 * 🔴 US-AIV2-0110 S27b (@UC0279 · rà độc lập làn A MEDIUM-2) — CHỮ CHUNG: nói LOẠI thứ, không nói thứ NÀO.
 *
 * Tập ĐÓNG (đã chuẩn hoá: bỏ dấu, thường): danh từ loại của ba ô quỹ/đợt/nhóm + chữ đệm/chỉ trỏ hay đi kèm. KHÔNG có chữ số và
 * không có tên riêng nào ⇒ phần riêng của một tên ("5A", "dã ngoại", "bóng đá") không bao giờ bị coi là chung. Tên ô của CHÍNH
 * ô đang resolve (`slotIdAsWords`) được cộng vào lúc gọi — tự khám phá, không danh sách tay các ô (self-check ở
 * `generic-ref.test.ts` đòi mọi ô quỹ/đợt/nhóm của Sổ thật có tên ô + danh từ tiếng Việt nằm trong tập).
 */
export const GENERIC_REF_WORDS: ReadonlySet<string> = new Set([
  // danh từ loại
  "quy", "tien", "nhom", "dot", "thu", "khoan", "ky",
  // chữ đệm / chỉ trỏ
  "cua", "cho", "nay", "do", "kia", "cai", "cac", "nhung", "ay", "trong", "vao",
  // 🔴 AI-REDUNDANT-ASK (@UC0279, probe 11/10): TRẠNG THÁI nghĩa vụ + động từ của việc — "nhắc mấy nhà chưa đóng tiền" ⇒ Phiếu gạch ô đợt = "chưa đóng
  // tiền" / "nhắc": nói AI cần nhắc, không nói đợt NÀO. Chữ chung ⇒ 1 đợt + ô tự điền như ô trống · ≥2 ⇒ HỎI cả danh sách. Phần riêng ("tháng 10") vẫn chặn.
  "dong", "nop", "chua", "nhac",
]);

/** Chữ (khác rỗng) mà MỌI token đều là chữ chung (tập đóng ∪ tên ô `slotId`) ⇒ không mang danh tính nào để khớp. */
export function isGenericRefText(text: string, slotId?: string): boolean {
  const tokens = normalizeRefText(text).split(" ").filter((t) => t !== "");
  if (tokens.length === 0) return false;
  const own = slotId === undefined ? [] : slotIdAsWords(slotId).split(" ");
  return tokens.every((t) => GENERIC_REF_WORDS.has(t) || own.includes(t));
}

function matchesRef(needle: string, needleTokens: readonly string[], ref: NamedRef): boolean {
  const label = normalizeRefText(ref.label);
  if (label === "") return false;
  if (label === needle) return true;
  const labelTokens = label.split(" ");
  return needleTokens.every((t) => labelTokens.includes(t));
}

export function resolveNamedRefSlot(
  text: string | null | undefined,
  refs: readonly NamedRef[] | undefined,
  /**
   * 🔴 US-AIV2-0023d (@UC0226) — VÌ SAO phạm vi rỗng, khi loader ĐO ĐƯỢC (quỹ thấy được nhưng đang đóng băng).
   * Chỉ dùng khi danh sách RỖNG: còn một mục thì phạm vi không rỗng, và một lý do "bị chặn" gắn vào ca bình
   * thường sẽ chặn oan cả trang. Vắng ⇒ `empty_scope` chung (không bịa nguyên nhân).
   */
  emptyReason?: SlotResolutionReason,
  /**
   * S27b (@UC0279): ô đang resolve — `id` của nó cũng là chữ chung (`isGenericRefText`); `autofill` quyết nhánh 1-mục của chữ chung
   * (rà vòng 2 LOW-2). Vắng ⇒ coi như ô KHÔNG tự điền (thận trọng: hỏi).
   */
  ownSlot?: { readonly id: string; readonly autofill?: unknown },
): SlotResolution<string> {
  if (refs === undefined || refs.length === 0) return slotNone(emptyReason ?? SLOT_RESOLUTION_REASON.EMPTY_SCOPE);
  const all = refs.map((r) => ({ value: r.id, label: r.label }));
  const rawId = (text ?? "").trim();
  if (rawId === "") {
    return refs.length === 1 ? slotResolved(refs[0]!.id, refs[0]!.label) : slotCandidates(SLOT_RESOLUTION_REASON.MISSING, all);
  }
  const byId = refs.find((r) => r.id === rawId);
  if (byId !== undefined) return slotResolved(byId.id, byId.label);

  const needle = normalizeRefText(rawId);
  if (needle === "") return slotCandidates(SLOT_RESOLUTION_REASON.UNMATCHED, all);
  // Nhãn bằng CHÍNH XÁC (sau chuẩn hoá) thắng nhãn chỉ chứa token — đúng 1 ⇒ resolved; ≥2 trùng tên ⇒ hỏi giữa chúng.
  const exact = refs.filter((r) => normalizeRefText(r.label) === needle);
  if (exact.length === 1) return slotResolved(exact[0]!.id, exact[0]!.label);
  /*
   * 🔴 S27b (@UC0279 · MEDIUM-2) — chữ CHUNG ("quỹ", "tiền", "nhóm", "đợt thu"…) ≡ KHÔNG nói gì. Trước bản vá nó đi đường khớp TOKEN
   * và CHỌN lặng lẽ mục duy nhất có chữ đó trong tên: "Quỹ lớp 5A" + "Tiền dã ngoại" ⇒ "quỹ" chọn "Quỹ lớp 5A", "tiền" chọn "Tiền dã
   * ngoại" (READ-DEBTS#33 lượt 2) · "Tháng 9/2026 — Quỹ lớp 5A" ⇒ `dot="quỹ"` (DEBT-REMIND#11, 3/3 lượt). Nay: ≥2 ⇒ HỎI cả danh sách.
   * Đúng 1 mục ⇒ tuỳ ô (rà vòng 2 LOW-2, hướng THẬN TRỌNG): ô CÓ `autofill` ⇒ như ô trống (luật tự điền — ô đó vốn đã tự điền khi
   * người dùng không nói gì); ô KHÔNG tự điền (PAY-ON-BEHALF `khoan` T2, READ-COLLECTIONS `dot`…) ⇒ HỎI một nút: chữ chung không
   * được GHIM ô vào mục duy nhất — trước bản vá, "quỹ" ghim `khoan` vào "Tháng 9/2026 — Quỹ lớp 5A" qua đường token.
   * Nhãn bằng CHÍNH XÁC đứng trước (một quỹ tên đúng là "Quỹ" vẫn khớp), nên đây không bao giờ biến một lần CHỌN đúng tên thành HỎI;
   * nó chỉ biến CHỌN-theo-chữ-chung thành HỎI (hoặc thành luật tự điền ở ô tự điền có 1 mục). Chữ SỐ không bao giờ là chữ chung ⇒
   * "kỳ 1" vẫn đi đường token và chọn "Kỳ 1/2026".
   */
  if (exact.length === 0 && isGenericRefText(rawId, ownSlot?.id)) {
    return refs.length === 1 && ownSlot?.autofill !== undefined ? slotResolved(refs[0]!.id, refs[0]!.label) : slotCandidates(SLOT_RESOLUTION_REASON.UNMATCHED, all);
  }
  const needleTokens = needle.split(" ");
  const hits = exact.length > 1 ? exact : refs.filter((r) => matchesRef(needle, needleTokens, r));
  if (hits.length === 1) return slotResolved(hits[0]!.id, hits[0]!.label);
  if (hits.length === 0) return slotCandidates(SLOT_RESOLUTION_REASON.UNMATCHED, all);
  // ≥2 khớp ⇒ hỏi kèm ĐÚNG các mục khớp — "lấy mục đầu" là đột biến AC1 phải đỏ.
  return slotCandidates(
    SLOT_RESOLUTION_REASON.AMBIGUOUS,
    hits.map((r) => ({ value: r.id, label: r.label })),
  );
}

/**
 * Nhãn tiếng Việt cho `periodKey` của kỳ thu (khớp `derivePeriodKey`: `YYYY-MM` · `YYYY-Qn` · `YYYY` · `YYYY-Www`).
 * Đợt thu không có tiêu đề thì nhãn này là thứ người dùng gọi tên ("tháng 9") — và là thứ luật khớp token đọc.
 * Không nhận ra ⇒ trả nguyên key (không bịa).
 */
export function periodKeyLabelVi(periodKey: string): string {
  const monthly = /^(\d{4})-(\d{2})$/u.exec(periodKey);
  if (monthly) return `Tháng ${Number.parseInt(monthly[2]!, 10)}/${monthly[1]}`;
  const quarterly = /^(\d{4})-Q([1-4])$/u.exec(periodKey);
  if (quarterly) return `Quý ${quarterly[2]}/${quarterly[1]}`;
  const weekly = /^(\d{4})-W(\d{2})$/u.exec(periodKey);
  if (weekly) return `Tuần ${Number.parseInt(weekly[2]!, 10)}/${weekly[1]}`;
  if (/^\d{4}$/u.test(periodKey)) return `Năm ${periodKey}`;
  return periodKey;
}
